'use client'
import { useEffect, useRef } from 'react'
import Image from 'next/image'

const PARALLAX_FACTOR = 0.12

const supportsScrollTimeline = () =>
  CSS.supports('animation-timeline', 'scroll()') || CSS.supports('animation-timeline', 'scroll(root)')

const PageHeaderParallaxBackground = () => {
  const layerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const layer = layerRef.current
    if (!layer) return

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    const cssTimeline = supportsScrollTimeline()

    let ticking = false
    let rafId = 0
    let jsFallbackActive = false

    const setEnd = () => {
      const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
      layer.style.setProperty('--parallax-end', `${-maxScroll * PARALLAX_FACTOR}px`)
    }

    const update = () => {
      ticking = false
      layer.style.transform = `translate3d(0, ${-window.scrollY * PARALLAX_FACTOR}px, 0)`
    }

    const onScroll = () => {
      if (ticking) return
      ticking = true
      rafId = window.requestAnimationFrame(update)
    }

    const stopJsFallback = () => {
      if (!jsFallbackActive) return
      window.removeEventListener('scroll', onScroll)
      window.cancelAnimationFrame(rafId)
      ticking = false
      rafId = 0
      jsFallbackActive = false
      layer.style.removeProperty('transform')
    }

    const startJsFallback = () => {
      if (jsFallbackActive) return
      jsFallbackActive = true
      update()
      window.addEventListener('scroll', onScroll, { passive: true })
    }

    const applyMotionPreference = () => {
      if (motionQuery.matches) {
        stopJsFallback()
        layer.style.removeProperty('transform')
        layer.style.removeProperty('--parallax-end')
        return
      }

      setEnd()
      if (!cssTimeline) startJsFallback()
    }

    applyMotionPreference()

    const ro = new ResizeObserver(() => {
      if (!motionQuery.matches) setEnd()
    })
    ro.observe(document.documentElement)
    window.addEventListener('resize', applyMotionPreference)
    motionQuery.addEventListener('change', applyMotionPreference)

    return () => {
      motionQuery.removeEventListener('change', applyMotionPreference)
      window.removeEventListener('resize', applyMotionPreference)
      ro.disconnect()
      stopJsFallback()
    }
  }, [])

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 -z-[1] overflow-hidden" aria-hidden="true">
      <div ref={layerRef} className="page-header-parallax-layer">
        <Image
          src="/images/bg-page-header.svg"
          alt=""
          width={1440}
          height={1774}
          sizes="100vw"
          className="h-auto w-full"
        />
      </div>
    </div>
  )
}

export default PageHeaderParallaxBackground
